/**
 * Установка AI-аналитика (docs/16 §7–8, E21-T11..T14).
 *
 * Секреты (root-пароль, LLM-ключ) в ответах не возвращаются: только
 * `hasPassword` / `hasApiKey`. Git-репозиторий — свойство проекта
 * (docs/17 §5.5), в настройках агента его нет.
 */

import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { UserSchema } from './user.js';

export const AgentInstallStatusSchema = z.enum([
  'not_installed',
  'queued',
  'installing',
  'installed',
  'failed',
  'uninstalling',
]);
export type AgentInstallStatus = z.infer<typeof AgentInstallStatusSchema>;

export const AgentInstallActionSchema = z.enum(['install', 'update', 'apply', 'uninstall']);
export type AgentInstallAction = z.infer<typeof AgentInstallActionSchema>;

export const AgentInstallJobStatusSchema = z.enum([
  'queued',
  'running',
  'succeeded',
  'failed',
  'cancelled',
]);
export type AgentInstallJobStatus = z.infer<typeof AgentInstallJobStatusSchema>;

export const AgentServerTestSchema = z.object({
  ok: z.boolean(),
  fingerprint: z.string().nullable(),
  os: z.string().nullable(),
  arch: z.string().nullable(),
  cpu: z.number().int().nullable(),
  memoryMb: z.number().int().nullable(),
  diskGb: z.number().int().nullable(),
  docker: z.boolean(),
  systemd: z.boolean(),
  testedAt: IsoDateTimeSchema,
});
export type AgentServerTest = z.infer<typeof AgentServerTestSchema>;

export const AgentLlmTestSchema = z.object({
  ok: z.boolean(),
  model: z.string(),
  latencyMs: z.number().int().nullable(),
  toolCalling: z.boolean(),
  testedAt: IsoDateTimeSchema,
});
export type AgentLlmTest = z.infer<typeof AgentLlmTestSchema>;

export const AgentStatusNamesSchema = z.object({
  trigger: z.string().min(1).max(80),
  work: z.string().min(1).max(80),
  review: z.string().min(1).max(80),
  approved: z.string().min(1).max(80),
});
export type AgentStatusNames = z.infer<typeof AgentStatusNamesSchema>;

export const AgentSetupSchema = z.object({
  agent: UserSchema,
  server: z.object({
    host: z.string(),
    port: z.number().int(),
    hasPassword: z.boolean(),
    fingerprint: z.string().nullable(),
    lastTest: AgentServerTestSchema.nullable(),
  }),
  llm: z.object({
    provider: z.string(),
    baseUrl: z.string().nullable(),
    model: z.string().nullable(),
    hasApiKey: z.boolean(),
    lastTest: AgentLlmTestSchema.nullable(),
  }),
  statuses: AgentStatusNamesSchema,
  install: z.object({
    status: AgentInstallStatusSchema,
    version: z.string().nullable(),
    installedAt: IsoDateTimeSchema.nullable(),
    lastJobId: UuidSchema.nullable(),
    lastError: z.string().nullable(),
  }),
  /** E25: общий агент на несколько проектов; `project` тогда `null`. */
  isShared: z.boolean().optional(),
  maxParallelJobs: z.number().int().min(1).max(10).optional(),
  engine: z.string().nullable().optional(),
  engineVersion: z.string().nullable().optional(),
  branchPrefix: z.string().optional(),
  dev: z
    .object({
      jobTimeoutMinutes: z.number().int(),
      maxDiffLines: z.number().int(),
      maxFiles: z.number().int(),
    })
    .optional(),
});
export type AgentSetup = z.infer<typeof AgentSetupSchema>;

/** Пределы своих навыков агента (docs/16 §4.9). */
export const AGENT_SKILL_MAX_BYTES = 64 * 1024;
export const AGENT_SKILLS_MAX_TOTAL_BYTES = 128 * 1024;
export const AGENT_SKILLS_MAX_FILES = 20;

export const AgentSkillFilenameSchema = z
  .string()
  .trim()
  .min(1, 'Укажите имя файла')
  .max(120, 'Имя файла не длиннее 120 символов')
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._-]*\.md$/,
    'Нужен markdown-файл: латинские буквы, цифры, . _ - и расширение .md',
  );

/** Навык без содержимого: список показывает имена и размер, текст не нужен. */
export const AgentSkillSchema = z.object({
  id: UuidSchema,
  filename: z.string(),
  sizeBytes: z.number().int(),
  updatedAt: IsoDateTimeSchema,
});
export type AgentSkill = z.infer<typeof AgentSkillSchema>;

export const AgentSkillsResponseSchema = z.object({ items: z.array(AgentSkillSchema) });
export type AgentSkillsResponse = z.infer<typeof AgentSkillsResponseSchema>;

export const UpsertAgentSkillBodySchema = z
  .object({ content: z.string().min(1, 'Файл пуст').max(AGENT_SKILL_MAX_BYTES) })
  .strict();
export type UpsertAgentSkillBody = z.infer<typeof UpsertAgentSkillBodySchema>;

const serverHost = z.string().trim().min(1).max(200);
const secret = z.string().min(1).max(2000);

export const UpdateAgentSetupBodySchema = z
  .object({
    serverHost: serverHost.optional(),
    serverPort: z.number().int().min(1).max(65535).optional(),
    serverPassword: secret.optional(),
    serverFingerprint: z.string().min(1).max(200).optional(),
    llmBaseUrl: z.string().url().max(500).optional(),
    llmModel: z.string().trim().min(1).max(200).optional(),
    llmApiKey: secret.optional(),
    statuses: AgentStatusNamesSchema.optional(),
    // E25: AI-разработчик.
    maxParallelJobs: z.number().int().min(1).max(10).optional(),
    devJobTimeoutMinutes: z.number().int().min(15).max(480).optional(),
    devMaxDiffLines: z.number().int().min(50).max(20_000).optional(),
    devMaxFiles: z.number().int().min(1).max(500).optional(),
    branchPrefix: z
      .string()
      .trim()
      .min(1)
      .max(30)
      .regex(/^[A-Za-z0-9._/-]+$/, 'Префикс ветки: латиница, цифры, . _ / -')
      .optional(),
  })
  .strict();
export type UpdateAgentSetupBody = z.infer<typeof UpdateAgentSetupBodySchema>;

export const UpdateAgentBodySchema = z
  .object({ version: z.string().trim().min(1).max(50).optional() })
  .strict();
export type UpdateAgentBody = z.infer<typeof UpdateAgentBodySchema>;

export const UninstallAgentBodySchema = z
  .object({
    confirmName: z.string().min(1).max(80),
    deleteData: z.boolean().default(false),
  })
  .strict();
export type UninstallAgentBody = z.infer<typeof UninstallAgentBodySchema>;

export const AgentInstallStepSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: z.enum(['pending', 'running', 'ok', 'failed', 'skipped']),
  startedAt: IsoDateTimeSchema.nullable().optional(),
  finishedAt: IsoDateTimeSchema.nullable().optional(),
});
export type AgentInstallStep = z.infer<typeof AgentInstallStepSchema>;

export const AgentInstallJobSchema = z.object({
  id: UuidSchema,
  agentId: UuidSchema,
  action: AgentInstallActionSchema,
  status: AgentInstallJobStatusSchema,
  step: z.string().nullable(),
  steps: z.array(AgentInstallStepSchema),
  logTail: z.string(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  version: z.string().nullable(),
  startedAt: IsoDateTimeSchema.nullable(),
  finishedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AgentInstallJob = z.infer<typeof AgentInstallJobSchema>;

export const AgentInstallJobResponseSchema = z.object({ job: AgentInstallJobSchema });
export type AgentInstallJobResponse = z.infer<typeof AgentInstallJobResponseSchema>;

export const AgentListRowSchema = z.object({
  id: UuidSchema,
  login: z.string(),
  displayName: z.string(),
  agentRole: z.string().nullable(),
  agentServerHost: z.string().nullable(),
  isShared: z.boolean().optional(),
  projectCount: z.number().int().optional(),
  installStatus: AgentInstallStatusSchema,
  installedVersion: z.string().nullable(),
  llmModel: z.string().nullable(),
});
export type AgentListRow = z.infer<typeof AgentListRowSchema>;

export const AgentListResponseSchema = z.object({ items: z.array(AgentListRowSchema) });
export type AgentListResponse = z.infer<typeof AgentListResponseSchema>;
